/**
 * Bots.
 *
 * Ein Bot benutzt ausschliesslich `moeglicheAktionen` und `anwenden` - genau die
 * Schnittstelle, die auch die Oberflaeche benutzt. Er sieht nichts, was ein Mensch
 * nicht auch sieht, und er kann nichts, was ein Mensch nicht auch koennte.
 *
 * Die Strategie ist bewusst schlicht und nachvollziehbar:
 * 1. Eingaenge sind die Einnahmequelle. Jeder bezahlbare Eingang wird gekauft.
 * 2. Gebaut wird dort, wo der Nachtsatz je ausgegebenem Geld am staerksten steigt.
 * 3. Grundstuecke werden gekauft, solange ein Bargeldpolster bleibt.
 * 4. In der Versteigerung wird bis zu einem Anteil des Buchwerts geboten.
 */
import { HOTELS } from './data/hotels'
import { FELDER } from './data/spielplan'
import type { Aktion, Zustand } from './typen'
import { anwenden, besitzVon, buchwert, moeglicheAktionen, spieler, stufeVon } from './spiel'

/** Bargeld, das ein Bot nicht freiwillig unterschreitet. */
const POLSTER = 1500
/** Anteil des Buchwerts, bis zu dem ein Bot in der Versteigerung mitgeht. */
const GEBOTSANTEIL = 0.7

function wert(a: Aktion, z: Zustand): number {
  const kasse = spieler(z, z.amZug).konto
  switch (a.art) {
    case 'wuerfeln':
    case 'uebernachten':
      return 1000

    case 'gratis-bauen':
    case 'gratis-freizeitpark':
      // Geschenke nimmt man immer.
      return 900

    case 'gratis-eingang':
      return 880 + blockadewert(z, a.feld)

    case 'eingang-kaufen': {
      const preis = HOTELS[a.hotel].eingang
      if (kasse - preis < POLSTER / 2) return -1
      return 700 + blockadewert(z, a.feld)
    }

    case 'freizeitpark-kaufen': {
      const h = HOTELS[a.hotel]
      if (kasse - h.freizeitpark.kosten < POLSTER) return -1
      const vorher = h.gebaeude[h.gebaeude.length - 1]!.nachtsatz
      return 500 + ((h.freizeitpark.nachtsatz - vorher) / h.freizeitpark.kosten) * 4000
    }

    case 'baugenehmigung': {
      const b = z.besitz[a.hotel]
      if (!b) return -1
      const h = HOTELS[a.hotel]
      let kosten = 0
      for (let i = 0; i < a.anzahl; i++) kosten += h.gebaeude[b.gebaeude + i]!.kosten
      // Die Genehmigung kann doppelt so teuer werden - das muss tragbar bleiben.
      if (kasse - kosten * 2 < 0) return -1
      const vorher = stufeVon(b)?.nachtsatz ?? 0
      const nachher = h.gebaeude[b.gebaeude + a.anzahl - 1]!.nachtsatz
      return 400 + ((nachher - vorher) / kosten) * 6000
    }

    case 'grundstueck-kaufen': {
      const h = HOTELS[a.hotel]
      if (kasse - h.grundstueck < POLSTER) return -1
      return 300 + (h.gebaeude[0]!.nachtsatz / h.grundstueck) * 2000
    }

    case 'zwangskauf': {
      const h = HOTELS[a.hotel]
      if (kasse - h.zwangsverkauf < POLSTER) return -1
      return 350 + (h.gebaeude[0]!.nachtsatz / h.zwangsverkauf) * 2000
    }

    case 'zug-beenden':
      return 0

    case 'zug-abgeben':
      // Ein Zusatzzug bringt fast immer mehr, als er kostet: mehr Felder heisst mehr
      // Kauf- und Baugelegenheiten. Nur wenn direkt vor uns eine teure fremde
      // Rechnung wartet, ist Verzicht klueger.
      return gefahrVoraus(z) ? 20 : -1

    default:
      return -1
  }
}

/**
 * Droht in Wurfweite eine Rechnung, die mehr kostet als das halbe Bargeld?
 * Sechs Felder voraus, gewichtet mit der Wahrscheinlichkeit, dort zu landen.
 */
function gefahrVoraus(z: Zustand): boolean {
  const s = spieler(z, z.amZug)
  let erwartet = 0
  for (let d = 1; d <= 6; d++) {
    const feld = (s.feld + d) % FELDER.length
    const eingang = z.eingaenge.find((e) => e.feld === feld)
    if (!eingang) continue
    const b = z.besitz[eingang.hotel]
    if (!b || b.besitzer === z.amZug) continue
    const satz = stufeVon(b)?.nachtsatz ?? 0
    // Im Mittel 3,5 Naechte, Chance auf dieses Feld ein Sechstel.
    erwartet += (satz * 3.5) / 6
  }
  return erwartet > s.konto * 0.5
}

/**
 * Ein Eingang an einem Feld, dessen andere Seite einem Gegner gehoert oder gehoeren
 * kann, ist doppelt wertvoll: er verdient Geld und blockiert den Nachbarn dauerhaft.
 */
function blockadewert(z: Zustand, feld: number): number {
  const f = FELDER[feld]
  if (!f) return 0
  const nachbarn = [f.innen, f.aussen].filter((x) => x !== null)
  if (nachbarn.length < 2) return 0
  const fremd = nachbarn.some((h) => {
    const b = z.besitz[h]
    return b && b.besitzer !== z.amZug
  })
  return fremd ? 120 : 40
}

/** Die Aktion, die dieser Bot in der aktuellen Lage waehlt. */
export function botAktion(z: Zustand): Aktion {
  const moeglich = moeglicheAktionen(z)
  if (moeglich.length === 0) return { art: 'zug-beenden' }

  if (z.phase === 'auktion' && z.auktion) {
    return auktionsZug(z)
  }

  if (z.phase === 'schuld') {
    // Das billigste Grundstueck zuerst hergeben - Perlen behaelt man.
    const sortiert = besitzVon(z, z.amZug)
      .slice()
      .sort((a, b) => buchwert(a) - buchwert(b))
    const opfer = sortiert[0]
    if (opfer) return { art: 'versteigern', hotel: opfer.hotel }
    return moeglich[0]!
  }

  let beste = moeglich[0]!
  let bester = -Infinity
  for (const a of moeglich) {
    const w = wert(a, z)
    if (w > bester) {
      bester = w
      beste = a
    }
  }
  return beste
}

function auktionsZug(z: Zustand): Aktion {
  const a = z.auktion!
  const b = z.besitz[a.hotel]
  const bieter = spieler(z, a.amZug)
  const wert = b ? buchwert(b) : 0
  const grenze = Math.min(bieter.konto, Math.floor(wert * GEBOTSANTEIL))
  // In sinnvollen Schritten steigern statt in Fuenfzigern - sonst dauert eine
  // Versteigerung um ein zehntausend teures Hotel zweihundert Runden.
  const schritt = Math.max(50, Math.round((wert * 0.05) / 50) * 50)
  const naechstes = Math.max(a.hoechstgebot + 50, a.hoechstgebot + schritt)
  if (a.hoechstgebot + 50 > grenze) return { art: 'passen' }
  return { art: 'bieten', betrag: Math.min(naechstes, grenze) }
}

/**
 * Spielt so lange automatisch weiter, wie ein Bot am Zug ist. Menschliche Spieler
 * bleiben unangetastet - der Aufrufer bekommt den Zustand zurueck, sobald ein Mensch
 * entscheiden muss.
 */
export function spieleBotsAb(z: Zustand, maxSchritte = 500): Zustand {
  let out = z
  for (let i = 0; i < maxSchritte; i++) {
    if (out.phase === 'ende') return out
    const handelnder =
      out.phase === 'auktion' && out.auktion ? out.auktion.amZug : out.amZug
    if (!spieler(out, handelnder).bot) return out
    out = anwenden(out, botAktion(out))
  }
  return out
}
